'use client';

import { c as _c } from "react/compiler-runtime";
import type { InterfaceToken } from '@morpho-org/dapp-core';
import { ZERO } from '@morpho-org/dapp-core';
import { ExternalLink } from '@morpho-org/ui-v6/server';
import { ArrowRight } from '@repo/ui/icons';
import { FORCED_DEALLOCATION_HELP_URL, IN_KIND_REDEMPTION_HELP_URL } from '@/config/external-urls';
import { formatFeeAmount, formatTokenAmount } from '@/helpers/formatters';
import InfoCallout from './InfoCallout';
interface InsufficientLiquidityCalloutProps {
  /** The serviceable ceiling: everything the plain withdraw path can pay out. */
  assets: bigint;
  /** Cash leg of the action on offer: the redemption's idle assets, or `assets` net of fee. */
  walletAssets: bigint;
  /** The redemption penalty, or the force-deallocation fee. Zero on the `limited` variant. */
  feeAssets: bigint;
  vaultAsset: InterfaceToken | null | undefined;
  isInKindRedemptionEligible: boolean;
  /** Fills the input with `assets`. */
  onAmountClick: () => void;
  variant: 'limited' | 'insufficient';
}
const InsufficientLiquidityCallout = t0 => {
  const $ = _c(27);
  const {
    assets,
    walletAssets,
    feeAssets,
    vaultAsset,
    isInKindRedemptionEligible,
    onAmountClick,
    variant
  } = t0;
  let t1;
  if ($[0] !== vaultAsset?.decimals || $[1] !== vaultAsset?.price || $[2] !== vaultAsset?.symbol) {
    t1 = amount => formatFeeAmount({
      price: vaultAsset?.price,
      amount,
      decimals: vaultAsset?.decimals,
      withTrailingZero: false,
      symbol: vaultAsset?.symbol
    });
    $[0] = vaultAsset?.decimals;
    $[1] = vaultAsset?.price;
    $[2] = vaultAsset?.symbol;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const formatFee = t1;
  let t2;
  if ($[4] !== vaultAsset) {
    t2 = amount_0 => formatTokenAmount(vaultAsset, amount_0);
    $[4] = vaultAsset;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const format = t2;
  const isWarning = variant === "insufficient";
  const canAutofill = assets > ZERO;
  let t3;
  if ($[6] !== format || $[7] !== onAmountClick) {
    t3 = label => <button type="button" className="cursor-pointer underline underline-offset-2" onClick={onAmountClick} data-testid="serviceable-withdraw-autofill">{format(label)}</button>;
    $[6] = format;
    $[7] = onAmountClick;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const amountButton = t3;
  const t4 = isWarning ? "warning" : "info";
  const t5 = isWarning ? "insufficient-liquidity-callout" : "limited-liquidity-callout";
  let t6;
  if ($[9] !== amountButton || $[10] !== assets || $[11] !== canAutofill || $[12] !== feeAssets || $[13] !== format || $[14] !== formatFee || $[15] !== isInKindRedemptionEligible || $[16] !== isWarning || $[17] !== walletAssets) {
    t6 = isWarning ? isInKindRedemptionEligible ? <>This withdraws up to the Vault's idle liquidity to your wallet. The remaining amount can only be withdrawn as a Market position you choose.{" "}{canAutofill ? <>You can withdraw up to {amountButton(assets)} to your wallet. </> : null}</> : <>{canAutofill ? amountButton(walletAssets) : format(walletAssets)} goes to your wallet{feeAssets > ZERO ? <>, after a {formatFee(feeAssets)} fee</> : null}. Your remaining balance is lent out in this Vault's Markets.{" "}</> : <>You can withdraw up to {canAutofill ? amountButton(assets) : format(assets)} to your wallet.{" "}{isInKindRedemptionEligible ? <>The rest is lent out to Markets listed by the vault, and can only be withdrawn as a Market position.{" "}</> : <>The rest is lent out in this Vault's Markets. </>}</>;
    $[9] = amountButton;
    $[10] = assets;
    $[11] = canAutofill;
    $[12] = feeAssets;
    $[13] = format;
    $[14] = formatFee;
    $[15] = isInKindRedemptionEligible;
    $[16] = isWarning;
    $[17] = walletAssets;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const t7 = isInKindRedemptionEligible ? IN_KIND_REDEMPTION_HELP_URL : FORCED_DEALLOCATION_HELP_URL;
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <ArrowRight className="ml-[2px] inline size-3 align-middle" />;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t7) {
    t9 = <ExternalLink href={t7} underlined={true}>Learn more{t8}</ExternalLink>;
    $[20] = t7;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t4 || $[23] !== t5 || $[24] !== t6 || $[25] !== t9) {
    t10 = <InfoCallout variant={t4} data-testid={t5} role="status" aria-live="polite">{t6}{t9}</InfoCallout>;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
};
export default InsufficientLiquidityCallout;